import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, RotateCcw, Trophy, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Inline from './Inline';
import Confetti from './Confetti';
import { useProgress } from '../hooks/useProgress';

export default function Quiz({ course, next }) {
  const { saveQuiz, quizScore } = useProgress();
  const [answers, setAnswers] = useState({});
  const [attempt, setAttempt] = useState(0);
  const total = course.quiz.length;
  const answered = Object.keys(answers).length;
  const score = course.quiz.filter((q, i) => answers[i] === q.answer).length;
  const finished = answered === total;

  const choose = (qi, oi) => {
    if (answers[qi] !== undefined) return;
    const nextAnswers = { ...answers, [qi]: oi };
    setAnswers(nextAnswers);
    if (Object.keys(nextAnswers).length === total) {
      saveQuiz(course.id, course.quiz.filter((q, i) => nextAnswers[i] === q.answer).length);
    }
  };

  const retry = () => {
    setAnswers({});
    setAttempt((a) => a + 1);
  };

  return (
    <div className="quiz" key={attempt}>
      {course.quiz.map((q, qi) => {
        const picked = answers[qi];
        return (
          <motion.section
            className="quiz-q"
            key={qi}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: qi * 0.06 }}
          >
            <h3><span className="quiz-num">Q{qi + 1}</span><Inline text={q.q} /></h3>
            <div className="quiz-options">
              {q.options.map((o, oi) => {
                const state = picked === undefined ? '' : oi === q.answer ? 'is-right' : oi === picked ? 'is-wrong' : 'is-dim';
                return (
                  <button type="button" key={oi} className={`quiz-option ${state}`} onClick={() => choose(qi, oi)} disabled={picked !== undefined}>
                    <span className="quiz-letter">{String.fromCharCode(65 + oi)}</span>
                    <span><Inline text={o} /></span>
                    {state === 'is-right' && <Check size={18} className="quiz-mark" />}
                    {state === 'is-wrong' && <X size={18} className="quiz-mark" />}
                  </button>
                );
              })}
            </div>
            <AnimatePresence>
              {picked !== undefined && (
                <motion.p
                  className={`quiz-explain ${picked === q.answer ? 'ok' : 'bad'}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  <strong>{picked === q.answer ? 'Correct. ' : 'Not quite. '}</strong>
                  <Inline text={q.explain} />
                </motion.p>
              )}
            </AnimatePresence>
          </motion.section>
        );
      })}

      <div className={`quiz-result ${finished ? 'is-done' : ''}`}>
        {finished && score === total && <Confetti count={36} />}
        <Trophy size={28} />
        <div>
          <strong>{finished ? `You scored ${score} of ${total}` : `${answered} of ${total} answered`}</strong>
          <p>
            {finished
              ? score === total
                ? 'Perfect score. This step is yours.'
                : 'Review the lessons for the questions you missed, then try again.'
              : quizScore(course.id) !== undefined
                ? `Your best so far: ${quizScore(course.id)} of ${total}.`
                : 'Pick an answer for each question to see your score.'}
          </p>
        </div>
        {finished && (
          <div className="quiz-actions">
            <button type="button" className="btn btn-ghost" onClick={retry}><RotateCcw size={16} /> Try again</button>
            {next && <Link className="btn btn-primary" to={`/course/${next.id}`}>Step {next.step}: {next.title} <ArrowRight size={16} /></Link>}
          </div>
        )}
      </div>
    </div>
  );
}
